import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import VerifiedIcon from '@mui/icons-material/Verified';
import { tokens } from "../../theme";
import { adminScrollPageSx } from "../../theme/adminLayout";
import VehicleLocationInsight from "../../components/VehicleLocationInsight";
import { adminAuthHeaders, getApiErrorMessage } from "../../utils/adminApi";
import { API_BASE_URL } from "../../config/apiBase";

const VehicleGetById = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/vehicle/getVehicleById/${id}`
        );
        if (response.data.status && response.data.result.vehicle) {
          setVehicle(response.data.result.vehicle);
        } else {
          throw new Error("Failed to fetch vehicle data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  const formatField = (field) => {
    if (field === null || field === undefined) return "N/A";
    if (typeof field === "boolean") return field ? "Yes" : "No";
    return field;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditClick = () => {
    navigate(`/update-vehicle/${id}`);
  };

  const handleApproveVehicle = async () => {
    const confirm = await Swal.fire({
      title: "Approve this vehicle?",
      text: "It will become visible for rental once approved.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2e7d32",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, approve",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setApproving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/vehicle/approveVehicle/${id}`,
        {},
        { headers: adminAuthHeaders() }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || "Vehicle approved successfully",
          severity: "success",
        });

        setVehicle((prev) => ({
          ...prev,
          vehicleApproved: true,
        }));
      } else {
        throw new Error(response.data.message || "Failed to approve vehicle");
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Error approving vehicle"),
        severity: "error",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const renderPhotoGallery = () => {
    const photos = [
      { key: "frontPhoto", label: "Front View" },
      { key: "backPhoto", label: "Back View" },
      { key: "sidePhoto1", label: "Side View 1" },
      { key: "sidePhoto2", label: "Side View 2" },
    ];

    const damagePhotos = [
      { key: "damagepic1", label: "Damage Photo 1" },
      { key: "damagepic2", label: "Damage Photo 2" },
      { key: "damagepic3", label: "Damage Photo 3" },
      { key: "damagepic4", label: "Damage Photo 4" },
    ];

    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: colors.blueAccent[600] }}>
            Vehicle Photos
          </Typography>
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={12} sm={6} md={3} key={photo.key}>
                {vehicle[photo.key] ? (
                  <Paper elevation={3} sx={{ 
                    p: 1, 
                    height: "100%",
                    background: '#ffffff',
                    border: `1px solid #e0e0e0`,
                    borderRadius: 2,
                  }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[500] }}>
                      {photo.label}
                    </Typography>
                    <img
                      src={vehicle[photo.key]}
                      alt={photo.label}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget.nextElementSibling;
                        if (fallback) fallback.style.display = "flex";
                      }}
                      style={{
                        width: "100%",
                        height: "160px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <Box
                      sx={{
                        display: "none",
                        height: "160px",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "8px",
                        bgcolor: colors.grey[100],
                      }}
                    >
                      <Chip label="Image unavailable" size="small" variant="outlined" />
                    </Box>
                  </Paper>
                ) : (
                  <Paper elevation={1} sx={{ 
                    p: 2, 
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.grey[100],
                    borderRadius: 2,
                  }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[500] }}>
                      {photo.label}
                    </Typography>
                    <Chip 
                      label="No Image" 
                      variant="outlined" 
                      size="small"
                      sx={{ backgroundColor: "white" }}
                    />
                  </Paper>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>

        {(vehicle?.damageType || vehicle?.damageDescription) && (
          <Grid item xs={12} sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: colors.blueAccent[600] }}>
              Damage Information
            </Typography>
            <Paper elevation={2} sx={{ 
              p: 3, 
              borderLeft: `4px solid ${colors.redAccent[500]}`,
              borderRadius: 2,
              mb: 3,
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Damage Type:
                  </Typography>
                  <Typography variant="body1">
                    {formatField(vehicle.damageType)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Damage Description:
                  </Typography>
                  <Typography variant="body1">
                    {formatField(vehicle.damageDescription)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2}>
              {damagePhotos.map((photo) => (
                <Grid item xs={12} sm={6} md={3} key={photo.key}>
                  {vehicle[photo.key] ? (
                    <Paper elevation={3} sx={{ 
                      p: 1, 
                      height: "100%",
                      background: '#ffffff',
                      border: `1px solid #e0e0e0`,
                      borderRadius: 2,
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[500] }}>
                        {photo.label}
                      </Typography>
                      <img
                        src={vehicle[photo.key]}
                        alt={photo.label}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.style.display = "flex";
                        }}
                        style={{
                          width: "100%",
                          height: "160px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <Box
                        sx={{
                          display: "none",
                          height: "160px",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          bgcolor: colors.grey[100],
                        }}
                      >
                        <Chip label="Image unavailable" size="small" variant="outlined" />
                      </Box>
                    </Paper>
                  ) : (
                    <Paper elevation={1} sx={{ 
                      p: 2, 
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: colors.grey[100],
                      borderRadius: 2,
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: colors.grey[500] }}>
                        {photo.label}
                      </Typography>
                      <Chip 
                        label="No Image" 
                        variant="outlined" 
                        size="small"
                        sx={{ backgroundColor: "white" }}
                      />
                    </Paper>
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const renderField = (key, value) => {
    // Skip fields that we don't want to render in the main details section
    const skipFields = [
      "Id", "SellerID", "VehicleTypeId", "createdAt", "updatedAt", 
      "frontPhoto", "backPhoto", "sidePhoto1", "sidePhoto2",
      "damagepic1", "damagepic2", "damagepic3", "damagepic4",
      "Seller", "VehicleType", "damageType", "damageDescription", "vehicleApproved",
      "vehicleLocation",
    ];
    
    if (skipFields.includes(key)) return null;

    // Format the field label
    const label = key
      .replace(/([A-Z])/g, " $1")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    // Custom icons for specific fields
    let icon = null;
    if (key === "fuelType") {
      icon = <LocalGasStationIcon sx={{ color: colors.blueAccent[500], fontSize: 20 }} />;
    } else if (key === "mileage") {
      icon = <SpeedIcon sx={{ color: colors.blueAccent[500], fontSize: 20 }} />;
    } else if (key === "perDayPrice" || key === "mileageFee") {
      icon = <AttachMoneyIcon sx={{ color: colors.blueAccent[500], fontSize: 20 }} />;
    } else if (key === "noOfSeats") {
      icon = <AirlineSeatReclineNormalIcon sx={{ color: colors.blueAccent[500], fontSize: 20 }} />;
    } else if (key === "vehicleLocation") {
      icon = <LocationOnIcon sx={{ color: colors.blueAccent[500], fontSize: 20 }} />;
    }

    return (
      <React.Fragment key={key}>
        <Grid item xs={12} md={4}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ 
              borderLeft: `3px solid ${colors.blueAccent[500]}`,
              pl: 1.5,
              lineHeight: 1.5,
              color: colors.blueAccent[500]
            }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          {key.includes("imageURL") || key.includes("Licence") ? (
            value ? (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Paper elevation={3} sx={{ 
                  padding: 1, 
                  display: 'inline-block', 
                  background: '#ffffff',
                  border: `1px solid #e0e0e0`,
                  borderRadius: 2,
                }}>
                  <img
                    src={value}
                    alt={label}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                </Paper>
              </Box>
            ) : (
              <Chip 
                label="No Image Available" 
                variant="outlined" 
                color="default"
                sx={{ mt: 1 }}
              />
            )
          ) : ["gpsnavigation", "AcOrNon", "insuranceFileVerfied"].includes(key) || key.startsWith("is") ? (
            <Chip
              icon={value === "yes" || value === true ? <CheckCircleIcon /> : <CancelIcon />}
              label={value === "yes" || value === true ? "Yes" : "No"}
              color={value === "yes" || value === true ? "success" : "error"}
              variant="filled"
              sx={{ fontWeight: "medium", mt: 1 }}
            />
          ) : icon ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {icon}
              <Typography variant="body1" sx={{ ml: 1 }}>
                {key === "perDayPrice" || key === "mileageFee" ? `$${formatField(value)}` : formatField(value)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mt: 1 }}>{formatField(value)}</Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ 
            my: 2,
            opacity: 0.6,
            borderColor: '#e0e0e0'
          }} />
        </Grid>
      </React.Fragment>
    );
  };

  const renderVehicleType = () => {
    if (!vehicle?.VehicleType) return null;
    
    return (
      <Grid item xs={12}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            backgroundColor: colors.primary[400],
          }}
        >
          {vehicle.VehicleType.imageURL && (
            <Avatar
              src={vehicle.VehicleType.imageURL}
              alt={vehicle.VehicleType.type}
              sx={{
                width: 60,
                height: 60,
                mr: 2,
                border: `2px solid ${colors.grey[100]}`,
              }}
            />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {vehicle.VehicleType.type}
            </Typography>
            <Typography variant="body2" color={colors.grey[400]}>
              Vehicle Type
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderSellerInfo = () => {
    if (!vehicle?.Seller) return null;
    
    return (
      <Grid item xs={12}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            borderLeft: `4px solid ${colors.greenAccent[500]}`,
          }}
        >
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: colors.greenAccent[500] }}>
            Seller Information
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {vehicle.Seller.imageURL ? (
              <Avatar
                src={vehicle.Seller.imageURL}
                alt={`${vehicle.Seller.firstName} ${vehicle.Seller.lastName}`}
                sx={{
                  width: 60,
                  height: 60,
                  mr: 2,
                  border: `2px solid ${colors.grey[300]}`,
                }}
              />
            ) : (
              <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: colors.greenAccent[500] }}>
                <PersonIcon />
              </Avatar>
            )}
            <Box>
              <Typography variant="h6" fontWeight="medium">
                {vehicle.Seller.firstName} {vehicle.Seller.lastName}
              </Typography>
              <Typography variant="body2" color={colors.grey[500]}>
                {vehicle.Seller.email} • {vehicle.Seller.contactNumber}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <LocationOnIcon sx={{ color: colors.grey[500], fontSize: 18, mr: 0.5 }} />
                <Typography variant="body2" color={colors.grey[500]}>
                  {vehicle.Seller.location || "Location not specified"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderApprovalStatus = () => {
    return (
      <Grid item xs={12}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            borderLeft: `4px solid ${vehicle?.vehicleApproved ? colors.greenAccent[500] : colors.redAccent[500]}`,
            backgroundColor: vehicle?.vehicleApproved ? colors.greenAccent[50] : colors.grey[50],
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              {vehicle?.vehicleApproved ? (
                <CheckCircleIcon sx={{ color: colors.greenAccent[500], fontSize: 30, mr: 2, mt: 0.5 }} />
              ) : (
                <CancelIcon sx={{ color: colors.redAccent[500], fontSize: 30, mr: 2, mt: 0.5 }} />
              )}
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="bold"
                  color={vehicle?.vehicleApproved ? colors.greenAccent[500] : colors.redAccent[500]}
                >
                  {vehicle?.vehicleApproved ? "Vehicle Approved" : "Vehicle Not Approved"}
                </Typography>
                <Typography variant="body2" color={colors.grey[600]}>
                  {vehicle?.vehicleApproved
                    ? "This vehicle has been approved and is available for rental."
                    : "This vehicle requires approval before it can be listed for rental."}
                </Typography>
              </Box>
            </Box>

            {!vehicle?.vehicleApproved && (
              <Button
                variant="contained"
                color="success"
                fullWidth={isMobile}
                startIcon={<VerifiedIcon />}
                onClick={handleApproveVehicle}
                disabled={approving}
                sx={{
                  fontWeight: "bold",
                  minWidth: { sm: 180 },
                  alignSelf: { xs: "stretch", sm: "center" },
                  "&:hover": {
                    bgcolor: colors.greenAccent[600],
                  },
                }}
              >
                {approving ? "Approving..." : "Approve Vehicle"}
              </Button>
            )}
          </Box>
        </Paper>
      </Grid>
    );
  };

  return (
    <Box sx={adminScrollPageSx}>
      {/* Header Section */}
      <Box
        mb={3}
        p={{ xs: 2, md: 3 }}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <DirectionsCarIcon sx={{ color: "white" }} />
            <Typography
              variant="h3"
              fontWeight="bold"
              color="white"
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" } }}
            >
              Vehicle Details #{id}
            </Typography>
          </Box>
          <Typography variant="body1" color="white">
            View detailed information about the vehicle
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{
            bgcolor: 'white',
            color: colors.blueAccent[500],
            '&:hover': {
              bgcolor: colors.grey[200],
            }
          }}
        >
          Edit Vehicle
        </Button> */}
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress color="primary" thickness={4} />
        </Box>
      ) : error ? (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mt: 2, 
          textAlign: 'center', 
          borderLeft: '4px solid #f44336',
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}>
          <Typography color="error" align="center" variant="h6">
            {error}
          </Typography>
        </Paper>
      ) : (
        <Card sx={{ 
          mt: 4, 
          mb: 4, 
          borderRadius: '12px',
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
          overflow: 'hidden',
          backgroundColor: "#ffffff",
        }}>
          <CardContent sx={{ padding: { xs: 2, sm: 3, md: 4 } }}>
            <Grid container spacing={3}>
              {/* Vehicle Type Card */}
              {renderVehicleType()}
              
              {/* Approval Status and Button */}
              {renderApprovalStatus()}

              <Grid item xs={12}>
                <VehicleLocationInsight
                  vehicleId={vehicle?.Id}
                  vehicleLocation={vehicle?.vehicleLocation}
                  sellerCity={vehicle?.Seller?.location}
                />
              </Grid>
              
              {/* Vehicle Basic Information */}
              <Grid item xs={12}>
                <Box sx={{ 
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: colors.blueAccent[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <DirectionsCarIcon sx={{ color: colors.blueAccent[600], mr: 1, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight="bold" color={colors.blueAccent[600]}>
                      {vehicle?.carMake?.toUpperCase()} ({vehicle?.year})
                    </Typography>
                  </Box>
                  <Chip 
                    label={vehicle?.transmission?.toUpperCase() || "N/A"} 
                    sx={{ 
                      bgcolor: colors.blueAccent[600], 
                      color: "white",
                      fontWeight: "bold" 
                    }} 
                  />
                </Box>
              </Grid>
              
              {/* Vehicle Details */}
              {vehicle &&
                Object.entries(vehicle).map(([key, value]) =>
                  renderField(key, value)
                )}
              
              {/* Vehicle Photos Gallery */}
              {renderPhotoGallery()}
              
              {/* Seller Information */}
              {renderSellerInfo()}
              
              {/* Created & Updated Date */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: colors.grey[100],
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: colors.grey[500], fontSize: 20 }} />
                    <Typography variant="body2" color={colors.grey[500]}>
                      Created: {formatDate(vehicle?.createdAt)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: colors.grey[100],
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: colors.grey[500], fontSize: 20 }} />
                    <Typography variant="body2" color={colors.grey[500]}>
                      Last Updated: {formatDate(vehicle?.updatedAt)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleGetById;